/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk
} from '@reduxjs/toolkit';
import _ from 'lodash';
import type { SetRequired } from 'type-fest';

import messages from '@ibm-watson/shared/modules/ia/pages/settings/messages';
import { updateAssistant } from '@ibm-watson/shared/modules/ia/store/assistants/actions';
import {
  selectAssistant,
  selectDialogSkillId,
  selectSkillReferencesFromAssistant
} from '@ibm-watson/shared/modules/ia/store/assistants/core-selectors';
import {
  selectDialogSkillIdFromEnvironment,
  selectDraftEnvironment,
  selectIsDialogEnabledInDraftEnvironment,
  selectSkillReferencesForEnvironment
} from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import {
  addErrorMessage,
  addMessage,
  ToastStatus
} from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { getSkill } from '@ibm-watson/shared/modules/ia/store/skills/actions';
import { createSkill } from '@ibm-watson/shared/modules/ia/store/skills/ducks/create';
import { getErrorString } from '@ibm-watson/shared/modules/ia/util/errorHandler';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { ApiStatus } from '../../../../../ts';
import { Assistant, Environment } from '../../../../../ts/assistant';
import { SkillType } from '../../../../../ts/skill';
import { CreateAsyncThunk, SkillsSlice } from '../../../../../ts/state';
import { clearRecognitionClusters } from '../../../../analytics/store/actions';
import {
  listEnvironments,
  updateEnvironment
} from '../../environments/actions';

/**
 * Toggles dialog skill. This action will first check if dialog is enabled -
 * if so, it will disable it. If dialog is disabled, it will first check if
 * a dialog skill exists - if so, enable it for the assistant. Else, create one
 * and enable it by default.
 *
 * @returns {AsyncThunk} The async action
 */
export const toggleDialogSkill = createAsyncThunk<void, void, CreateAsyncThunk>(
  'skills/toggle-dialog',
  async (__, { dispatch, getState }) => {
    const hasDialogSkill = selectIsDialogEnabledInDraftEnvironment(getState());
    try {
      const assistant = selectAssistant(getState());
      const environment = selectDraftEnvironment(getState());
      // FIXME: We can change it later, for now we use this to figure out if we are updating assistant or environment when toggling dialog. See https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/56589
      const isNewStore = Boolean(assistant?.assistant_skills);
      const dialogSkillId = isNewStore
        ? selectDialogSkillIdFromEnvironment(
            getState(),
            environment?.environment_id
          )
        : selectDialogSkillId(getState());
      const assistantSkillReferences = _.cloneDeep(
        selectSkillReferencesFromAssistant(assistant)
      );
      const targetAssistantSkill = assistantSkillReferences.find(
        skillRef => skillRef.skill_id === dialogSkillId
      );

      const environmentSkillReferences = _.cloneDeep(
        selectSkillReferencesForEnvironment(
          getState(),
          environment?.environment_id
        )
      );
      const targetEnvironmentSkill = environmentSkillReferences.find(
        skillRef => skillRef.skill_id === dialogSkillId
      );

      // If dialog is enabled, disable it
      if (hasDialogSkill) {
        if (isNewStore) {
          // FIXME: Update environment if using newer version of store, also remove duplicating this logic. See https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/56589
          targetEnvironmentSkill.disabled = true;
          const data: SetRequired<
            Partial<Environment>,
            'environment_id' | 'assistant_id'
          > = {
            assistant_id: assistant?.assistant_id,
            environment_id: environment?.environment_id,
            skill_references: environmentSkillReferences
          };
          await dispatch(updateEnvironment(data));
        } else {
          targetAssistantSkill.disabled = true;
          const data: Partial<Assistant> = {
            assistant_id: assistant?.assistant_id,
            skill_references: assistantSkillReferences
          };
          await dispatch(updateAssistant(data));
        }

        dispatch(
          addMessage({
            message: messages.deactivatedDialogSuccess,
            status: ToastStatus.SUCCESS
          })
        );
        ctaEvents.settingsDisableDialog();
      }
      // Else dialog is disabled, enable it
      else {
        // If no dialog skill exists, create one and enable it by default
        if (!dialogSkillId) {
          const result = await dispatch(
            createSkill({
              name: `${assistant?.name}-dialog`,
              description: assistant?.description,
              language: assistant?.language,
              assistant_id: assistant?.assistant_id,
              type: SkillType.DIALOG,
              sample: false
            })
          ).unwrap();
          if (result?.skill_id) {
            //we need to fetch the dialog skill as the skill metadata is not returned when the skill is first created
            await dispatch(getSkill(result.skill_id)).unwrap();
          }
        }
        // Else, dialog skill exists and just enable it
        else {
          if (isNewStore) {
            targetEnvironmentSkill.disabled = false;
            const data: SetRequired<
              Partial<Environment>,
              'environment_id' | 'assistant_id'
            > = {
              assistant_id: assistant?.assistant_id,
              environment_id: environment?.environment_id,
              skill_references: environmentSkillReferences
            };
            await dispatch(updateEnvironment(data));
          } else {
            targetAssistantSkill.disabled = false;
            const data: Partial<Assistant> = {
              assistant_id: assistant?.assistant_id,
              skill_references: assistantSkillReferences
            };
            await dispatch(updateAssistant(data));
          }
        }
        dispatch(
          addMessage({
            message: messages.activatedDialogSuccess,
            status: ToastStatus.SUCCESS
          })
        );
        ctaEvents.settingsEnableDialog();
      }
      //clear recognition clusters when dialog is toggled
      dispatch(clearRecognitionClusters());
      await dispatch(listEnvironments());
    } catch (err) {
      dispatch(addErrorMessage(err));
      if (hasDialogSkill) {
        ctaEvents.settingsDisableDialog({
          resultValue: getErrorString(err),
          successFlag: false
        });
      } else {
        ctaEvents.settingsEnableDialog({
          resultValue: getErrorString(err),
          successFlag: false
        });
      }
      throw err;
    }
  }
);

/**
 * Resets Toggle dialog skill status.
 * @returns {void}
 */
export const resetToggleDialogStatus = createAction(
  'skills/reset-toggle-dialog'
);

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(toggleDialogSkill.pending, (state, _action) => {
      state.toggleDialogStatus = ApiStatus.LOADING;
      state.toggleDialogError = null;
    })
    .addCase(toggleDialogSkill.fulfilled, (state, _action) => {
      state.toggleDialogStatus = ApiStatus.SUCCEEDED;
      state.toggleDialogError = null;
    })
    .addCase(toggleDialogSkill.rejected, (state, action) => {
      state.toggleDialogStatus = ApiStatus.ERROR;
      state.toggleDialogError = action.error;
    })
    // Reset Toggle dialog status
    .addCase(resetToggleDialogStatus, (state, _action) => {
      state.toggleDialogStatus = ApiStatus.IDLE;
      state.toggleDialogError = null;
    });
};

export default reducer;
